import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchIcon, FilterIcon, MapPinIcon, CheckCircleIcon, GridIcon, ListIcon, MapIcon, StarIcon, ChevronRightIcon } from 'lucide-react';
import { developers } from '../utils/data';
import { Developer } from '../utils/types';
import Pagination from '../components/ui/Pagination';
import { TalentCapsuleCard } from '../components/developers/TalentCapsuleCard';
type ViewMode = 'grid' | 'spotlight' | 'collections' | 'map';
const Developers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filters, setFilters] = useState({
    status: '',
    role: '',
    position: '',
    country: '',
    skills: [] as string[]
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const developersPerPage = 8;
  // Certification exams data with ALL required properties
  const certificationExams = [{
    id: '1',
    name: 'Laravel Advanced Certification',
    description: 'Master advanced Laravel concepts including queues, events, broadcasting, and advanced Eloquent techniques.',
    price: 149,
    currency: 'USD',
    duration: '180',
    questions: 50,
    technologies: ['Laravel', 'PHP', 'MySQL'],
    topics: ['Advanced Eloquent', 'Queue Management', 'Event Broadcasting', 'API Development', 'Testing'],
    level: 'Advanced',
    badge: 'https://via.placeholder.com/80?text=Laravel'
  }, {
    id: '2',
    name: 'Vue.js Professional Certification',
    description: 'Comprehensive Vue.js certification covering Composition API, Vuex, Vue Router, and best practices.',
    price: 129,
    currency: 'USD',
    duration: '150',
    questions: 45,
    technologies: ['Vue.js', 'JavaScript', 'Vuex'],
    topics: ['Composition API', 'State Management', 'Vue Router', 'Component Design', 'Performance'],
    level: 'Professional',
    badge: 'https://via.placeholder.com/80?text=Vue'
  }, {
    id: '3',
    name: 'Full-Stack Laravel Developer',
    description: 'Complete full-stack certification combining Laravel backend with modern frontend frameworks.',
    price: 199,
    currency: 'USD',
    duration: '240',
    questions: 60,
    technologies: ['Laravel', 'Vue.js', 'React', 'API Development'],
    topics: ['Backend Architecture', 'Frontend Integration', 'API Design', 'Authentication', 'Deployment'],
    level: 'Expert',
    badge: 'https://via.placeholder.com/80?text=Full-Stack'
  }, {
    id: '4',
    name: 'React & Laravel Integration',
    description: 'Learn to build modern SPAs with React frontend and Laravel API backend.',
    price: 139,
    currency: 'USD',
    duration: '180',
    questions: 40,
    technologies: ['React', 'Laravel', 'REST APIs'],
    topics: ['React Fundamentals', 'Laravel API', 'State Management', 'Authentication'],
    level: 'Intermediate',
    badge: 'https://via.placeholder.com/80?text=React'
  }, {
    id: '5',
    name: 'Angular Professional Certification',
    description: 'Master Angular framework with TypeScript, RxJS, and enterprise-level application development.',
    price: 139,
    currency: 'USD',
    duration: '180',
    questions: 45,
    technologies: ['Angular', 'TypeScript', 'RxJS'],
    topics: ['Components & Modules', 'Reactive Programming', 'State Management', 'Testing', 'Performance'],
    level: 'Professional',
    badge: 'https://via.placeholder.com/80?text=Angular'
  }, {
    id: '6',
    name: 'Nuxt.js Expert Certification',
    description: 'Build production-ready Vue.js applications with Nuxt.js, covering SSR, SSG, and modern deployment.',
    price: 129,
    currency: 'USD',
    duration: '150',
    questions: 40,
    technologies: ['Nuxt.js', 'Vue.js', 'Node.js'],
    topics: ['Server-Side Rendering', 'Static Generation', 'API Routes', 'Deployment', 'SEO Optimization'],
    level: 'Expert',
    badge: 'https://via.placeholder.com/80?text=Nuxt'
  }];
  // Quick filter chips
  const quickFilters = [{
    label: 'Laravel',
    type: 'skill',
    value: 'Laravel'
  }, {
    label: 'Vue.js',
    type: 'skill',
    value: 'Vue.js'
  }, {
    label: 'React',
    type: 'skill',
    value: 'React'
  }, {
    label: 'Full-stack',
    type: 'role',
    value: 'Full-stack Developer'
  }, {
    label: 'Available now',
    type: 'status',
    value: 'available'
  }, {
    label: '<€75/hr',
    type: 'rate',
    value: '75'
  }];
  // Get unique values for filters
  const countries = [...new Set(developers.map(dev => dev.country))];
  const roles = [...new Set(developers.map(dev => dev.role))];
  const positions = [...new Set(developers.map(dev => dev.position))];
  const statuses = ['available', 'unavailable', 'looking'];
  const allSkills = [...new Set(developers.flatMap(dev => dev.skills.map(s => s.name)))];
  // Filter developers
  const filteredDevelopers = developers.filter(dev => {
    const matchesSearch = searchTerm === '' || dev.name.toLowerCase().includes(searchTerm.toLowerCase()) || dev.title.toLowerCase().includes(searchTerm.toLowerCase()) || dev.skills.some(skill => skill.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filters.status === '' || dev.status === filters.status;
    const matchesRole = filters.role === '' || dev.role === filters.role;
    const matchesPosition = filters.position === '' || dev.position === filters.position;
    const matchesCountry = filters.country === '' || dev.country === filters.country;
    const matchesSkills = filters.skills.length === 0 || filters.skills.every(skillName => dev.skills.some(s => s.name === skillName));
    return matchesSearch && matchesStatus && matchesRole && matchesPosition && matchesCountry && matchesSkills;
  });
  // Pagination
  const indexOfLastDeveloper = currentPage * developersPerPage;
  const indexOfFirstDeveloper = indexOfLastDeveloper - developersPerPage;
  const currentDevelopers = filteredDevelopers.slice(indexOfFirstDeveloper, indexOfLastDeveloper);
  const totalPages = Math.ceil(filteredDevelopers.length / developersPerPage);
  const handleQuickFilter = (type: string, value: string) => {
    if (type === 'skill') {
      setFilters(prev => {
        if (prev.skills.includes(value)) {
          return {
            ...prev,
            skills: prev.skills.filter(s => s !== value)
          };
        } else {
          return {
            ...prev,
            skills: [...prev.skills, value]
          };
        }
      });
    } else if (type === 'status') {
      setFilters(prev => ({
        ...prev,
        status: prev.status === value ? '' : value
      }));
    } else if (type === 'role') {
      setFilters(prev => ({
        ...prev,
        role: prev.role === value ? '' : value
      }));
    }
  };
  const clearFilters = () => {
    setFilters({
      status: '',
      role: '',
      position: '',
      country: '',
      skills: []
    });
    setSearchTerm('');
    setCurrentPage(1);
  };
  const isFilterActive = (type: string, value: string) => {
    if (type === 'skill') return filters.skills.includes(value);
    if (type === 'status') return filters.status === value;
    if (type === 'role') return filters.role === value;
    return false;
  };
  return <div className="bg-[#0F1115] min-h-screen text-gray-300" data-id="element-804">
      {/* Editorial Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F1115] via-[#1a1d24] to-[#111827] text-white overflow-hidden" data-id="element-805">
        {/* Subtle grain texture */}
        <div className="absolute inset-0 opacity-[0.015] bg-grain" data-id="element-806"></div>

        {/* PHP Code Background Image */}
        <div className="absolute inset-0 opacity-[0.15] overflow-hidden" data-id="element-807">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/koTEbxE6H3NVaj4P9TF4mh/Schermafbeelding_2025-10-11_150904.png" alt="PHP Code Background" className="w-full h-full object-cover object-center" data-id="element-808" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative z-10" data-id="element-809">
          <div className="max-w-4xl mx-auto text-center" data-id="element-810">
            <h1 className="font-display text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight" data-id="element-811">
              Verified developers you can{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-812">
                hire with confidence
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 mb-10 leading-relaxed" data-id="element-813">
              Search certified back- & front-end talent. Real badges, live
              availability, transparent rates.
            </p>

            {/* Primary & Secondary CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-814">
              <a href="#browse" className="bg-[#d73b3e] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-200 shadow-lg hover:shadow-xl" data-id="element-815">
                Browse certified talent
              </a>
              <Link to="/verification" className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-200 border border-white/20" data-id="element-816">
                How verification works
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400" data-id="element-817">
              <div className="flex items-center gap-2" data-id="element-818">
                <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-819" />
                <span data-id="element-820">3,500+ developers certified since 2018</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-821">
                <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-822" />
                <span data-id="element-823">Avg. 7 days to shortlist</span>
              </div>
            </div>
          </div>
        </div>

        {/* Curved divider */}
        <div className="absolute bottom-0 left-0 right-0" data-id="element-824">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" data-id="element-825">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#0F1115" data-id="element-826" />
          </svg>
        </div>
      </section>

      {/* Browse Section Title */}
      <section className="py-16 bg-[#0F1115]" data-id="element-827">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-828">
          <div className="max-w-3xl mx-auto text-center" data-id="element-829">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 leading-tight" data-id="element-830">
              Browse{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-831">
                Certified Developers
              </span>
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-832">
              <Link to="/developers" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/20" data-id="element-833">
                Browse Certified Developers
              </Link>
              <Link to="/signup" className="px-8 py-4 bg-[#d73b3e] text-white rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-834">
                Register (FREE) as a Certified Developer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12" id="browse" data-id="element-835">
        {/* Discovery Modes & Quick Filters */}
        <div className="mb-8" data-id="element-836">
          {/* View Mode Toggles */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6" data-id="element-837">
            <div className="flex gap-2 bg-[#1a1d24] rounded-full p-1 shadow-sm border border-gray-800" data-id="element-838">
              <button onClick={() => setViewMode('grid')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'grid' ? 'bg-[#d73b3e] text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-839">
                <GridIcon className="h-4 w-4 inline mr-1" data-id="element-840" />
                Grid
              </button>
              <button onClick={() => setViewMode('spotlight')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'spotlight' ? 'bg-[#d73b3e] text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-841">
                <StarIcon className="h-4 w-4 inline mr-1" data-id="element-842" />
                Spotlight
              </button>
              <button onClick={() => setViewMode('collections')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'collections' ? 'bg-[#d73b3e] text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-843">
                <ListIcon className="h-4 w-4 inline mr-1" data-id="element-844" />
                Collections
              </button>
              <button onClick={() => setViewMode('map')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'map' ? 'bg-[#d73b3e] text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-845">
                <MapIcon className="h-4 w-4 inline mr-1" data-id="element-846" />
                Map
              </button>
            </div>

            <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="lg:hidden flex items-center gap-2 px-4 py-2 bg-[#1a1d24] rounded-full border border-gray-800 text-sm font-medium text-gray-300 hover:bg-gray-800 transition-colors duration-200" data-id="element-847">
              <FilterIcon className="h-4 w-4" data-id="element-848" />
              Advanced filters
            </button>
          </div>

          {/* Quick Filter Chips */}
          <div className="flex flex-wrap gap-2 mb-6" data-id="element-849">
            {quickFilters.map(filter => <button key={filter.label} onClick={() => handleQuickFilter(filter.type, filter.value)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isFilterActive(filter.type, filter.value) ? 'bg-[#d73b3e] text-white shadow-md' : 'bg-[#1a1d24] text-gray-300 border border-gray-800 hover:border-gray-700 hover:text-white'}`} data-id="element-850">
                {filter.label}
              </button>)}
            {(filters.skills.length > 0 || filters.status || filters.role) && <button onClick={clearFilters} className="px-4 py-2 rounded-full text-sm font-medium text-[#d73b3e] hover:bg-red-500/10 transition-colors duration-200" data-id="element-851">
                Clear all
              </button>}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-852">
          {/* Advanced Filters Sidebar */}
          <div className={`lg:w-1/4 ${isFilterOpen ? 'block' : 'hidden lg:block'}`} data-id="element-853">
            <div className="bg-[#1a1d24] rounded-2xl p-6 sticky top-24 shadow-sm border border-gray-800" data-id="element-854">
              <div className="flex justify-between items-center mb-6" data-id="element-855">
                <h2 className="text-lg font-semibold text-white" data-id="element-856">
                  Narrow results
                </h2>
              </div>

              {/* Search */}
              <div className="mb-6" data-id="element-857">
                <label htmlFor="search" className="block text-sm font-medium text-gray-400 mb-2" data-id="element-858">
                  Search
                </label>
                <div className="relative" data-id="element-859">
                  <input type="text" id="search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full rounded-xl border border-gray-700 bg-[#0F1115] text-white px-4 py-3 pr-10 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm placeholder-gray-600" placeholder="Name, title, or skill..." data-id="element-860" />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-861">
                    <SearchIcon className="h-4 w-4 text-gray-500" data-id="element-862" />
                  </div>
                </div>
              </div>

              {/* Status Filter */}
              <div className="mb-6" data-id="element-863">
                <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-864">
                  Status
                </label>
                <select value={filters.status} onChange={e => setFilters({
                ...filters,
                status: e.target.value
              })} className="block w-full rounded-xl border border-gray-700 bg-[#0F1115] text-white px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-865">
                  <option value="" data-id="element-866">All Statuses</option>
                  {statuses.map(status => <option key={status} value={status} data-id="element-867">
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>)}
                </select>
              </div>

              {/* Role Filter */}
              <div className="mb-6" data-id="element-868">
                <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-869">
                  Role
                </label>
                <select value={filters.role} onChange={e => setFilters({
                ...filters,
                role: e.target.value
              })} className="block w-full rounded-xl border border-gray-700 bg-[#0F1115] text-white px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-870">
                  <option value="" data-id="element-871">All Roles</option>
                  {roles.map(role => <option key={role} value={role} data-id="element-872">
                      {role}
                    </option>)}
                </select>
              </div>

              {/* Position Filter */}
              <div className="mb-6" data-id="element-873">
                <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-874">
                  Position
                </label>
                <select value={filters.position} onChange={e => setFilters({
                ...filters,
                position: e.target.value
              })} className="block w-full rounded-xl border border-gray-700 bg-[#0F1115] text-white px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-875">
                  <option value="" data-id="element-876">All Positions</option>
                  {positions.map(position => <option key={position} value={position} data-id="element-877">
                      {position}
                    </option>)}
                </select>
              </div>

              {/* Country Filter */}
              <div className="mb-6" data-id="element-878">
                <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-879">
                  Country
                </label>
                <select value={filters.country} onChange={e => setFilters({
                ...filters,
                country: e.target.value
              })} className="block w-full rounded-xl border border-gray-700 bg-[#0F1115] text-white px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-880">
                  <option value="" data-id="element-881">All Countries</option>
                  {countries.map(country => <option key={country} value={country} data-id="element-882">
                      {country}
                    </option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Developers Grid */}
          <div className="lg:w-3/4" data-id="element-883">
            {/* Results count */}
            <div className="flex justify-between items-center mb-6" data-id="element-884">
              <p className="text-sm text-gray-400" data-id="element-885">
                Showing{' '}
                <span className="font-semibold text-white" data-id="element-886">
                  {filteredDevelopers.length}
                </span>{' '}
                certified developers
              </p>
            </div>

            {filteredDevelopers.length === 0 ? <div className="bg-[#1a1d24] rounded-2xl p-12 text-center border border-gray-800" data-id="element-887">
                <div className="mb-6" data-id="element-888">
                  <SearchIcon className="h-16 w-16 text-gray-600 mx-auto" data-id="element-889" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2" data-id="element-890">
                  No exact matches
                </h3>
                <p className="text-gray-400 mb-6" data-id="element-891">
                  Try widening your rate or region filters
                </p>
                <div className="flex flex-wrap gap-2 justify-center" data-id="element-892">
                  <button onClick={() => setFilters({
                ...filters,
                country: ''
              })} className="px-4 py-2 rounded-full text-sm font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors duration-200" data-id="element-893">
                    EU
                  </button>
                  <button onClick={() => setFilters({
                ...filters,
                status: 'available'
              })} className="px-4 py-2 rounded-full text-sm font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors duration-200" data-id="element-894">
                    Remote
                  </button>
                  <button onClick={clearFilters} className="px-4 py-2 rounded-full text-sm font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors duration-200" data-id="element-895">
                    €50–€90/hr
                  </button>
                </div>
              </div> : <>
                {viewMode === 'grid' && <div className="grid md:grid-cols-2 gap-6" data-id="element-896">
                    {currentDevelopers.map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-897" />)}
                  </div>}

                {viewMode === 'spotlight' && <div className="space-y-6" data-id="element-898">
                    {/* Featured large card */}
                    {currentDevelopers.length > 0 && <TalentCapsuleCard developer={currentDevelopers[0]} featured data-id="element-899" />}
                    {/* Two smaller cards */}
                    <div className="grid md:grid-cols-2 gap-6" data-id="element-900">
                      {currentDevelopers.slice(1, 3).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-901" />)}
                    </div>
                    {/* Rest in grid */}
                    <div className="grid md:grid-cols-2 gap-6" data-id="element-902">
                      {currentDevelopers.slice(3).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-903" />)}
                    </div>
                  </div>}

                {viewMode === 'collections' && <div className="space-y-8" data-id="element-904">
                    <div data-id="element-905">
                      <h3 className="text-2xl font-bold mb-4 leading-tight" data-id="element-906">
                        New{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-907">
                          This Week
                        </span>
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6" data-id="element-908">
                        {currentDevelopers.slice(0, 4).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-909" />)}
                      </div>
                    </div>
                    <div data-id="element-910">
                      <h3 className="text-2xl font-bold text-white mb-4" data-id="element-911">
                        New This Week
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6" data-id="element-912">
                        {currentDevelopers.slice(4).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-913" />)}
                      </div>
                    </div>
                  </div>}

                {viewMode === 'map' && <div className="bg-[#1a1d24] rounded-2xl p-8 border border-gray-800 text-center" data-id="element-914">
                    <MapIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" data-id="element-915" />
                    <h3 className="text-xl font-semibold text-white mb-2" data-id="element-916">
                      Map view coming soon
                    </h3>
                    <p className="text-gray-400" data-id="element-917">
                      Visualize developers by location on an interactive map
                    </p>
                  </div>}
              </>}

            {/* Pagination */}
            {filteredDevelopers.length > 0 && viewMode === 'grid' && <div className="mt-8 flex justify-center" data-id="element-918">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-919" />
              </div>}
          </div>
        </div>
      </div>

      {/* Certification Exams Section */}
      <section id="certifications" className="py-20 bg-gradient-to-br from-[#1a1d24] to-[#0F1115]" data-id="element-920">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-921">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-922">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight text-white" data-id="element-923">
              Get{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500" data-id="element-924">
                Certified
              </span>
            </h2>
            <p className="text-lg text-gray-400" data-id="element-925">
              Validate your expertise with industry-recognized certifications
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-926">
            {certificationExams.map((exam, index) => <div key={exam.id} className="bg-gradient-to-br from-[#1a1d24] to-[#0F1115] rounded-3xl shadow-xl border border-gray-800 hover:border-gray-700 hover:shadow-2xl transition-all duration-300 overflow-hidden group" data-id="element-927">
                <div className="p-8" data-id="element-928">
                  {/* Header with Badge and Price */}
                  <div className="flex items-start justify-between mb-6" data-id="element-929">
                    {/* Hexagon Badge */}
                    <div className="relative" data-id="element-930">
                      <div className="w-24 h-24 relative" data-id="element-931">
                        {/* Hexagon shape using clip-path */}
                        <div className={`absolute inset-0 ${index === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500' : 'bg-gradient-to-br from-orange-500 to-red-500'}`} style={{
                      clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
                    }} data-id="element-932"></div>
                        <div className="absolute inset-[3px] bg-white" style={{
                      clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
                    }} data-id="element-933">
                          <div className="w-full h-full flex flex-col items-center justify-center p-3" data-id="element-934">
                            <img src={exam.name.includes('Vue') ? 'https://upload.wikimedia.org/wikipedia/commons/9/95/Vue.js_Logo_2.svg' : exam.name.includes('React') ? 'https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg' : exam.name.includes('Angular') ? 'https://upload.wikimedia.org/wikipedia/commons/c/cf/Angular_full_color_logo.svg' : exam.name.includes('Nuxt') ? 'https://upload.wikimedia.org/wikipedia/commons/a/ae/Nuxt_logo.svg' : 'https://uploadthingy.s3.us-west-1.amazonaws.com/6raD3sQbYhrAYJrpu7Fmsg/Laravel.jpg'} alt={exam.name} className="w-10 h-10 object-contain mb-1" data-id="element-935" />
                            <span className="text-[8px] font-bold text-gray-700 text-center leading-tight" data-id="element-936">
                              Certification
                              <br data-id="element-937" />
                              for Laravel
                            </span>
                            <span className={`text-[10px] font-bold mt-1 ${index === 0 ? 'text-yellow-600' : 'text-orange-600'}`} data-id="element-938">
                              {index === 0 ? 'Junior' : 'Senior'}
                            </span>
                            <span className={`text-[10px] font-bold ${index === 0 ? 'text-yellow-600' : 'text-orange-600'}`} data-id="element-939">
                              Laravel
                            </span>
                            <span className={`text-[10px] font-bold ${index === 0 ? 'text-yellow-600' : 'text-orange-600'}`} data-id="element-940">
                              Developer
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="text-right" data-id="element-941">
                      <div className="text-4xl font-bold text-white mb-1" data-id="element-942">
                        ${index === 0 ? '89' : '199'},-
                      </div>
                      <div className="text-xs text-gray-400 underline" data-id="element-943">
                        Economic Discount/Parity
                      </div>
                      <div className="text-xs text-gray-400 underline" data-id="element-944">
                        Pricing available*
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-6" data-id="element-945">
                    {index === 0 ? 'Student Developer Exam' : 'Junior Developer Exam'}
                  </h3>

                  {/* Content with checkmarks */}
                  <div className="space-y-3 mb-8" data-id="element-946">
                    <div className="flex items-start gap-3" data-id="element-947">
                      <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-948">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-949" />
                      </svg>
                      <p className="text-sm text-gray-300 leading-relaxed" data-id="element-950">
                        {index === 0 ? <>
                            <span className="font-bold" data-id="element-951">The first stage</span>{' '}
                            is all about the basics for starting Laravel
                            Developers and students.
                          </> : <>
                            <span className="font-bold" data-id="element-952">The second stage</span>{' '}
                            is for junior Laravel developers with a{' '}
                            <span className="font-bold" data-id="element-953">
                              practical coding experience of 1 to 2 years.
                            </span>
                          </>}
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-954">
                      <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-955">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-956" />
                      </svg>
                      <p className="text-sm text-gray-300 leading-relaxed" data-id="element-957">
                        {index === 0 ? <>
                            <span className="font-bold" data-id="element-958">
                              45 Multiple choice questions, 100% theoretical
                              questions,
                            </span>{' '}
                            1-hour time limit.
                          </> : <>
                            <span className="font-bold" data-id="element-959">
                              45 Multiple choice questions, 50% theoretical
                              questions, 50% code example questions,
                            </span>{' '}
                            1-hour time limit.
                          </>}
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-960">
                      <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-961">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-962" />
                      </svg>
                      <p className="text-sm text-gray-300 leading-relaxed" data-id="element-963">
                        <span className="font-bold" data-id="element-964">75%</span> of the questions{' '}
                        <span className="font-bold" data-id="element-965">
                          need to be correct to pass,
                        </span>{' '}
                        if you fail you can take a free re-take within 1 month.
                      </p>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button className="w-full px-6 py-3.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-bold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg hover:shadow-xl text-sm" data-id="element-966">
                    Buy Now
                  </button>

                  {/* Footer note */}
                  <p className="text-[10px] text-gray-400 mt-4 leading-relaxed" data-id="element-967">
                    * We offer parity pricing, if you are eligible for an
                    economic discount, please apply to receive your discount
                    code.
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0F1115] border-t border-gray-800" data-id="element-968">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-969">
          <div className="max-w-3xl mx-auto text-center" data-id="element-970">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 leading-tight" data-id="element-971">
              Browse{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-972">
                Certified Developers
              </span>
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-973">
              <Link to="/developers" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/20" data-id="element-974">
                Browse Certified Developers
              </Link>
              <Link to="/signup" className="px-8 py-4 bg-[#d73b3e] text-white rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-975">
                Register (FREE) as a Certified Developer
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default Developers;